import { fetchDetailCouponTips } from '@/api/product';
import { IReviewsData } from '@/api/types/product';
import { IGoodsDetail, IProductDetail } from '@/api/types/public';
import Wish from '@/components/Product/Wish';
import StarRating from '@/components/StarRating';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import {
  CouponData,
  getPromotionStyle,
  processCouponTips,
  scrollToReviewList,
} from '@/utils/goods-detail-utils';
import { formatNumber, getDisplayStars, getIntlLocale, numberUnitFormat } from '@/utils/tools';
import { Button, Skeleton } from '@mui/material';
import { RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CouponSection from '../CouponSection';
import DetailCountDown from '../DetailCountDown';
import MemberTag from '../MemberTag';
import PromotionRules, { PromotionData } from '../PromotionRules';
import Relations from '../Relations';
import ClientPrice from './ClientPrice';
import PromotionScheduleTag from './PromotionScheduleTag';
const DetailCouponDialog = dynamic(() => import('../dialog/DetailCouponDialog'), { ssr: false });

interface ProductInfoProps {
  product: IProductDetail;
  clientRenderReady: boolean;
  getGlassesFilter: string;
  onShowSizeGuide: () => void;
  customer: IGoodsDetail['customer'];
  reviewsData: IReviewsData;
  priceInfo: {
    sell_price: number;
    price: number;
  };
  refreshPageData: () => void;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
  SDProductDetailsPageRelatedMktExp?: (params: any, index: number) => void;
  SDProductDetailsPageRelatedMktClick?: (params: any, index: number) => void;
  promotionScheduleData?: any;
}

// 主组件
const ProductInfo: React.FC<ProductInfoProps> = ({
  product,
  clientRenderReady,
  getGlassesFilter,
  onShowSizeGuide,
  customer,
  reviewsData,
  priceInfo,
  refreshPageData,
  SDProductDetailsPageBtnClick,
  SDProductDetailsPageRelatedMktExp,
  SDProductDetailsPageRelatedMktClick,
  promotionScheduleData,
}) => {
  const locale = getIntlLocale(env.NEXT_PUBLIC_SITE_NAME);
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const isShowDtBanner = useStore((state) => state.isShowDtBanner);
  const waiMode = useStore((state) => state.waiMode);
  const { trackEvent } = useReporting();
  const [showCouponsDialog, setShowCouponsDialog] = useState(false);
  const [couponData, setCouponData] = useState<any>({});
  const [couponTips, setCouponTips] = useState<string>('');
  // ! pureT是给 工具函数用的，官方明确表示next-intl不能用在utils.ts这种文件中
  // ! 同时，为了能在utils.ts文件中能正常使用i18n的编辑器扩展，所以不写prefix
  const pureT = useTranslations('');
  const t = useTranslations('goodsDetail');
  const tSizeGuide = useTranslations('goodsDetail.sizeGuide');
  // 获取优惠券提示
  const getDetailCouponTips = useCallback(async (isLoginSuccess?: boolean) => {
    // 登录成功后的埋点
    if (isLoginSuccess) {
      try {
        trackEvent(
          {
            eventName: 'product_sign',
            properties: {
              event_label: 'Sign_up',
              event_category: 'PRODUCT_DETAIL',
            },
          },
          [ProviderType.GA4],
        );
      } catch (error) {
        logger.log(error);
      }
    }

    try {
      const res = await fetchDetailCouponTips();
      if (res.code === 200) {
        const couponResponse = res.data as CouponData;
        setCouponData(couponResponse);

        // 使用工具函数处理优惠券提示
        const tips = processCouponTips(couponResponse, currency, pureT);
        if (tips) {
          setCouponTips(tips);
        }
      }
    } catch (error) {
      logger.log(error);
    }
  }, []);

  useEffect(() => {
    getDetailCouponTips();
  }, [getDetailCouponTips]);

  // 扩展产品对象
  const extendedProduct = product as any;

  // 计算促销类型
  const promotionData = useMemo((): PromotionData => {
    const promotionsType = String(product.products_promotions_type);

    if (promotionsType === '3') return { type: 'seckill' };
    if (promotionsType === '4') return { type: 'promotion' };
    if (userInfo?.is_member === 1 && product[`discount_percent_v${userInfo.member_level}`] > 0) {
      return { type: 'member' };
    }
    if (promotionsType === '2') return { type: 'flash-sale' };
    if (['0', '1'].includes(promotionsType) && userInfo?.is_member && userInfo.is_member !== 1) {
      return { type: 'no-member' };
    }
    if (userInfo?.is_member === 1 && product[`discount_percent_v${userInfo.member_level}`] <= 0) {
      return { type: 'ordinary' };
    }
    return { type: 'tourist' };
  }, [product, userInfo]);

  // 判断是否显示优惠券标签
  const showCouponTag = useMemo(() => {
    // 正常状态 ，是会员，但是不满足门槛
    const rule1 = promotionData.type === 'ordinary';
    // 游客状态
    const rule2 = promotionData.type === 'tourist';
    // 会员状态
    const rule3 = promotionData.type === 'member';
    // flash sale 或是秒杀的情况下 非会员不展示
    const rule4 =
      ['seckill', 'flash-sale', 'promotion'].includes(promotionData.type) &&
      userInfo?.is_member !== 2;
    return rule1 || rule2 || rule3 || rule4;
  }, [promotionData.type, userInfo]);

  const sizeFilter = useCallback((size: string) => {
    switch (size) {
      case 'S':
        return t('params.values.narrow');
      case 'M':
        return t('params.values.medium');
      case 'L':
        return t('params.values.wide');
      default:
        return size;
    }
  }, []);

  const showPromotionFlag = useMemo(() => {
    return (
      ['flash-sale', 'seckill', 'promotion'].includes(promotionData.type) ||
      [3, 4].includes(Number(product.products_promotions_type))
    );
  }, [promotionData.type, product.products_promotions_type]);

  return (
    <div className="w-full bg-white">
      <div className="mx-auto relative bg-white">
        {/* 商品基本信息 */}
        <div className="bg-white">
          <div className="flex items-center justify-between">
            <h1 className="text-[max(1.6666666667vw,16px)] font-bb leading-[1]">
              {product.short_name}
            </h1>
            {clientRenderReady ? (
              <Wish
                data={{
                  id: product?.id || 0,
                  shop_sku: product?.shop_sku || '',
                  name: product?.short_name || '',
                  short_name: product?.short_name || '',
                  wish_num: product?.wish_num || 0,
                  is_wish: customer?.is_wish || 2,
                  business_sku: product?.business_sku || product?.shop_sku || '',
                  sku: product?.sku || product?.shop_sku || '',
                }}
                className="flex items-center"
                iconClassName=""
                wishNumClassName="text-12 text-center block notranslate ml-[4px]"
                iconSize="22px"
                showWishNum
                loginSituation="商详页收藏"
                collectScene="商详页收藏"
              />
            ) : (
              <Skeleton variant="rectangular" width={40} height={22} />
            )}
          </div>
          <h2
            className={`pt-[6px] pb-10 text-[14px] capitalize ${waiMode ? 'text-primary-black' : 'text-[#a9a9a9]'}`}
          >
            <span>{product.product_frame_shape}</span>{' '}
            <span>{product.product_frame_color_name}</span>{' '}
            <span>{product.product_frame_material}</span> <span>{getGlassesFilter}</span>
          </h2>
          <section className="flex items-center mb-[32px]">
            <StarRating
              value={getDisplayStars(reviewsData.stars)}
              precision={0.5}
              readOnly
              size="small"
              iconSize="12px"
              gap="6px"
            />
            <div
              className={`review-box ml-8 cursor-pointer text-14 ${waiMode ? 'text-primary-black' : 'text-[#a9a9a9]'}`}
              onClick={() => {
                scrollToReviewList(isShowDtBanner ? 200 : 168);
                SDProductDetailsPageBtnClick?.('View More Reviews');
              }}
            >
              <span>{formatNumber(reviewsData.stars || 5, 1, locale)}</span>
              {reviewsData.all_count > 0 && (
                <span className="ml-4  hover:underline hover:text-primary-black">
                  ({numberUnitFormat(reviewsData.all_count)} {t('customerReivewsList.reviews')})
                </span>
              )}
            </div>
          </section>
          {showPromotionFlag && (
            <div
              className="flex justify-between items-center h-[26px] rounded-t-[4px] px-12"
              style={getPromotionStyle(product as IProductDetail)}
            >
              <span className="text-[#282828] font-bb text-[14px]">
                {promotionData.type === 'flash-sale'
                  ? t('flashSale')
                  : extendedProduct.activity_style?.flash_killing_title}
              </span>
              {extendedProduct.count_down && extendedProduct.count_down > 0 && clientRenderReady ? (
                <DetailCountDown
                  time={extendedProduct.count_down}
                  refreshPageData={refreshPageData}
                />
              ) : null}
            </div>
          )}
          <div
            className={`${showPromotionFlag ? 'border-1 border-[#d4d4d4] py-16 pb-[5px] px-12 rounded-b-[4px] border-t-0' : ''}`}
          >
            <div className={`flex items-center flex-wrap gap-[8px]`}>
              {/* 价格信息 */}
              <ClientPrice
                product={product}
                refreshPageData={refreshPageData}
                clientRenderReady={clientRenderReady}
              />
              {/* 促销活动标签 */}
              <PromotionScheduleTag
                data={promotionScheduleData}
                SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              />
              {/* 会员标签 */}
              <MemberTag
                userInfo={userInfo}
                priceInfo={priceInfo}
                currency={currency}
                promotionType={promotionData.type}
                product={product}
                isWeb
              />
            </div>
            {/* 优惠券和积分 */}
            <CouponSection
              showCouponTag={showCouponTag}
              couponTips={couponTips}
              product={product}
              onShowCoupons={() => setShowCouponsDialog(true)}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
              isWeb
            />
            {/* 促销规则 */}
            <PromotionRules promotionData={promotionData} product={extendedProduct} isWeb />
          </div>
          {/* Frame color */}
          {!!product.product_frame_color_name && (
            <div className="mt-32 text-16">
              {product.is_glass === 1 ? <span>{t('frameColor')}</span> : <span>{t('color')}</span>}
              <span className="font-bb ml-6">{product.product_frame_color_name}</span>
            </div>
          )}

          {/* 关联商品 */}
          {product.correlation && product.correlation.length > 0 && (
            <Relations
              list={product.correlation}
              product={product}
              onRelatedClick={(item, index) => {
                SDProductDetailsPageRelatedMktClick?.(item, index);
              }}
              onRelatedExposure={(item, index) => {
                SDProductDetailsPageRelatedMktExp?.(item, index);
              }}
              isWeb
            />
          )}
        </div>
        {/* 尺寸信息 */}
        {product.is_glass === 1 && (
          <div className="flex justify-between items-center text-[14px] pt-[32px] bg-white">
            <div>
              {product.product_size && (
                <p className="text-[#282828] text-16">
                  <span className="mr-4">{t('size')}</span>
                  <span>{sizeFilter(product.product_size)}</span>(
                  <span>{Math.round(Number(product?.glass_width))}</span>-
                  <span>{Math.round(Number(product?.glass_height))}</span>-
                  <span>{Math.round(Number(product?.frame_leg_length))}</span> {tSizeGuide('mm')})
                </p>
              )}
            </div>
            <Button
              size="small"
              onClick={() => {
                onShowSizeGuide();
                SDProductDetailsPageBtnClick?.('Size Chart');
              }}
              className={`flex items-center text-[12px] normal-case`}
              endIcon={<RightArrowOutlined />}
              sx={{
                padding: '0px',
                color: waiMode ? colors.primaryBlack : '#808080',
                fontSize: '12px',
                '& .MuiButton-endIcon': {
                  marginLeft: '4px',
                  fontSize: '14px',
                  color: '#808080',
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              {t('sizeChart')}
            </Button>
          </div>
        )}

        {/* 优惠券对话框 */}
        <DetailCouponDialog
          showCouponsDialog={showCouponsDialog}
          setShowCouponsDialog={setShowCouponsDialog}
          couponData={couponData}
          isWeb
        />
      </div>
    </div>
  );
};

export default ProductInfo;
